import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr, runJob } from "@/lib/api";
import { ui } from "@/lib/ui";
import { Loader2, Sparkles, ArrowLeft, Wand2, Clock, Globe2, Users } from "lucide-react";
import { Field, Section, ChipInput } from "@/components/national/Fields";

function LogoCard({ name, profile, logo, setLogo }) {
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!name.trim() || !profile) return toast.error("Enter a brand name and pick a category first");
    setBusy(true);
    try {
      const data = await runJob("/logo/preview", { business_name: name, category: profile.category_name });
      setLogo(data);
      toast.success("Logo ready — it'll be applied to this site");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Section title="Brand Logo">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Auto-create a professional logo from the brand name (~30–60s). Its colors become the site colors.</p>
        <button type="button" data-testid="nat-generate-logo" onClick={gen} disabled={busy} className={ui.btnGhost}>
          {busy ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4 text-indigo-400" />} {logo?.logo_url ? "Regenerate" : "Generate Logo"}
        </button>
      </div>
      {logo?.logo_url && (
        <div>
          <div className="rounded-lg bg-white border border-slate-800 p-5 flex items-center justify-center"><img data-testid="nat-logo-preview" src={logo.logo_url} alt="logo" style={{ maxHeight: 88, maxWidth: "100%" }} /></div>
          {logo.palette?.length > 0 && <div className="flex gap-2 mt-3 items-center"><span className="text-xs text-slate-500 font-mono uppercase">Colors</span>{logo.palette.map((c, i) => <span key={i} className="w-6 h-6 rounded border border-slate-700" style={{ background: c.hex }} />)}</div>}
        </div>
      )}
    </Section>
  );
}

const Fixed = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2.5">
    <Icon className="w-4 h-4 text-indigo-400 shrink-0" />
    <div><p className="text-[11px] uppercase tracking-wider font-mono text-slate-500">{label}</p><p className="text-sm text-slate-200">{value}</p></div>
  </div>
);

export default function NationalForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [f, setF] = useState({ name: "", domain: "" });
  const [selected, setSelected] = useState([]);
  const [extra, setExtra] = useState([]);
  const [logo, setLogo] = useState(null);
  const [loaded, setLoaded] = useState(!id);
  const [saving, setSaving] = useState(false);
  const profile = profiles.find((p) => p.id === profileId);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    api.get("/profiles").then(({ data: profs }) => {
      if (cancelled) return;
      setProfiles(profs);
      if (!id) { if (profs[0]) { setProfileId((cur) => { if (!cur) setSelected(profs[0].services.map((s) => s.name)); return cur || profs[0].id; }); } return; }
      api.get(`/national/${id}`).then(({ data }) => {
        if (cancelled) return;
        const n = data.national;
        const prof = profs.find((p) => p.id === n.category_profile_id) || profs.find((p) => p.category_name === n.category);
        const names = prof ? prof.services.map((s) => s.name) : [];
        setF({ name: n.name, domain: n.domain });
        setProfileId(prof?.id || "");
        setSelected(n.services.map((s) => s.name).filter((x) => names.includes(x)));
        setExtra(n.services.map((s) => s.name).filter((x) => !names.includes(x)));
        if (n.logo_url) setLogo({ logo_url: n.logo_url, palette: data.theme_override || [] });
        setLoaded(true);
      }).catch((e) => toast.error(apiErr(e)));
    }).catch(() => toast.error("Failed to load categories"));
    return () => { cancelled = true; };
  }, [id]);

  const pickProfile = (pid) => {
    setProfileId(pid);
    const p = profiles.find((x) => x.id === pid);
    setSelected(p ? p.services.map((s) => s.name) : []);
  };
  const toggle = (n) => setSelected((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));

  const submit = async () => {
    if (!profileId) return toast.error("Pick a category");
    if (!f.name.trim() || !f.domain.trim()) return toast.error("Fill in brand name and domain");
    const services = [...selected, ...extra].map((name) => ({ name, description: "" }));
    if (!services.length) return toast.error("Select at least one service");
    const body = { ...f, category_profile_id: profileId, services, logo_url: logo?.logo_url || "", theme_override: logo?.palette || [] };
    setSaving(true);
    try {
      if (id) {
        await api.put(`/national/${id}`, body);
        await api.post(`/national/${id}/regenerate`);
        toast.success("Saved — regenerating the website");
        navigate(`/national/${id}`);
      } else {
        const { data } = await api.post("/national", body);
        toast.success("Generating your national website…");
        navigate(`/national/${data.id}`);
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const input = (k, props) => <input data-testid={`nat-${k}`} className={ui.input} value={f[k]} onChange={(e) => set(k, e.target.value)} {...props} />;
  if (!loaded) return <div className="p-10 text-slate-500"><Loader2 className="w-5 h-5 spin" /></div>;

  return (
    <div className="animate-fade-up max-w-4xl space-y-5" data-testid="national-form">
      <div>
        {id ? <Link to={`/national/${id}`} className="text-sm text-slate-400 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to dashboard</Link>
          : <Link to="/create" className="text-sm text-slate-400 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Local business website instead</Link>}
        <h1 className={`${ui.pageHeader} mt-2`}>{id ? "Edit National Website" : "New National Website"}</h1>
        <p className={`${ui.muted} mt-1`}>A complete multi-page brand website written for a nationwide audience — no address needed.</p>
      </div>
      <Section title="Brand">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Field label="Category" required>
            <select data-testid="nat-category" className={ui.input} value={profileId} onChange={(e) => pickProfile(e.target.value)}>
              {!profileId && <option value="">Select a category</option>}
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.category_name}</option>)}
            </select>
          </Field></div>
          <Field label="Website / brand name" required>{input("name", { placeholder: "General HVAC Solutions" })}</Field>
          <Field label="Domain name" required hint="Where the site will be published">{input("domain", { placeholder: "generalhvacsolutions.com" })}</Field>
        </div>
        <p className="text-xs text-slate-500">No phone, email or address on the national site — contact details live on each business subpage.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1" data-testid="nat-fixed-settings">
          <Fixed icon={Globe2} label="Target market" value="Nationwide" />
          <Fixed icon={Users} label="Target audience" value="Homeowners, property owners & managers" />
          <Fixed icon={Clock} label="Business hours" value="Open 24 hours, 7 days" />
        </div>
      </Section>
      <Section title="Services">
        <p className="text-xs text-slate-500 -mt-2">Each selected service gets its own page. FAQs and About content are written automatically.</p>
        <div className="grid sm:grid-cols-2 gap-2" data-testid="nat-services">
          {profile?.services.map((s) => (
            <label key={s.anchor} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-800/60 cursor-pointer text-sm text-slate-200">
              <input type="checkbox" data-testid={`nat-service-${s.anchor}`} checked={selected.includes(s.name)} onChange={() => toggle(s.name)} className="w-4 h-4 accent-indigo-500" />
              {s.name}
            </label>
          ))}
        </div>
        <Field label="Additional services (optional)"><ChipInput value={extra} onChange={setExtra} placeholder="Type a service and press Enter" testid="nat-extra-services" /></Field>
      </Section>
      <LogoCard name={f.name} profile={profile} logo={logo} setLogo={setLogo} />
      <div className="flex justify-end">
        <button data-testid="nat-submit" onClick={submit} disabled={saving} className={ui.btnPrimary}>
          {saving ? <Loader2 className="w-4 h-4 spin" /> : <Sparkles className="w-4 h-4" />} {id ? "Save & Regenerate" : "Generate National Website"}
        </button>
      </div>
    </div>
  );
}
